/**
 * INT-1787945890 — hold Klaviyo *popup / fullscreen* images until the overlay
 * is actually on screen.
 *
 * Klaviyo injects `<img class="needsclick">` from
 * d3k81ch9hvuctc.cloudfront.net the moment a form is eligible. That fetch is
 * what made Home / Collection LCP `img.needsclick`. Theme Liquid cannot
 * `image_url` those files; this only delays the request.
 *
 * Must run as a classic blocking <head> script *before* `content_for_header`
 * so the src setter is patched before the Klaviyo onsite app embed executes.
 *
 * Leaves embeds alone (e.g. `klaviyo-form-Yna2nv` on-page "Join the pack").
 * Does not change popup trigger timing, copy, or offer.
 */
(function (root) {
  'use strict';

  var HELD_ATTR = 'data-cc-klaviyo-src';
  var HOST_RE = /d3k81ch9hvuctc\.cloudfront\.net/i;
  /* Embed ids are 6 chars and start with A–Z (`Yna2nv`). Reject `klaviyo-form-button`. */
  var EMBED_FORM_RE = /(?:^|\s)klaviyo-form-[A-Z][A-Za-z0-9]{5}(?:\s|$)/;
  var CLOSE_SEL = '.klaviyo-close-form';
  var FORM_SEL = 'form.klaviyo-form';
  var SAFETY_RELEASE_MS = 12000;

  function isKlaviyoImageUrl(url) {
    if (!url || typeof url !== 'string') return false;
    var trimmed = url.trim();
    if (!trimmed) return false;
    if (trimmed.indexOf('data:') === 0 || trimmed.indexOf('about:') === 0 || trimmed.charAt(0) === '#') {
      return false;
    }
    return HOST_RE.test(trimmed);
  }

  function classNameOf(el) {
    if (!el) return '';
    var c = el.className;
    if (typeof c === 'string') return c;
    if (c && typeof c.baseVal === 'string') return c.baseVal;
    return '';
  }

  function hasEmbedFormClass(className) {
    return EMBED_FORM_RE.test(className || '');
  }

  function isOverlayOpenFromBox(box) {
    if (!box) return false;
    if (box.display === 'none' || box.visibility === 'hidden') return false;
    if (typeof box.opacity === 'number' ? box.opacity === 0 : parseFloat(box.opacity) === 0) {
      return false;
    }
    return Number(box.width) > 2 && Number(box.height) > 2;
  }

  function computedBox(el) {
    if (!el || !el.ownerDocument || !el.ownerDocument.defaultView) return null;
    var cs = el.ownerDocument.defaultView.getComputedStyle(el);
    var rect = typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : { width: 0, height: 0 };
    return {
      display: cs.display,
      visibility: cs.visibility,
      opacity: cs.opacity,
      position: cs.position,
      zIndex: cs.zIndex,
      width: rect.width,
      height: rect.height
    };
  }

  function isFixedKlaviyoOverlay(el) {
    var box = computedBox(el);
    if (!box || box.position !== 'fixed') return false;
    var z = parseInt(box.zIndex, 10);
    if (!isFinite(z) || z < 1000) return false;
    if (!el.querySelector) return false;
    return !!(el.querySelector(CLOSE_SEL) || el.querySelector(FORM_SEL));
  }

  function findPopupOverlay(el) {
    var node = el;
    while (node && node.nodeType === 1) {
      if (isFixedKlaviyoOverlay(node)) return node;
      node = node.parentElement;
    }
    return null;
  }

  function isKlaviyoEmbedContext(el) {
    var node = el;
    while (node && node.nodeType === 1) {
      if (hasEmbedFormClass(classNameOf(node))) {
        var box = computedBox(node);
        if (box && box.position === 'fixed') return false;
        return true;
      }
      node = node.parentElement;
    }
    return false;
  }

  function shouldHoldKlaviyoSrc(el, url) {
    if (!isKlaviyoImageUrl(url)) return false;
    if (el && el.getAttribute && el.getAttribute('data-cc-klaviyo-allow') === '1') return false;
    if (isKlaviyoEmbedContext(el)) return false;
    if (!el || !el.isConnected) return true;
    var overlay = findPopupOverlay(el);
    if (overlay) return !isOverlayOpenFromBox(computedBox(overlay));
    return true;
  }

  function install(win) {
    var w = win || root;
    if (!w || !w.document || w.CCKlaviyoImageDeferInstalled) return;
    w.CCKlaviyoImageDeferInstalled = true;

    var Img = w.HTMLImageElement;
    var El = w.Element;
    if (!Img || !Img.prototype || !El || !El.prototype) return;

    var srcDesc = Object.getOwnPropertyDescriptor(Img.prototype, 'src');
    if (!srcDesc || typeof srcDesc.get !== 'function' || typeof srcDesc.set !== 'function') return;

    var nativeSetAttribute = El.prototype.setAttribute;

    function hold(img, url) {
      nativeSetAttribute.call(img, HELD_ATTR, url);
      try {
        nativeSetAttribute.call(img, 'fetchpriority', 'low');
      } catch (e) {
        /* older WebKit */
      }
    }

    function release(img) {
      var url = img.getAttribute(HELD_ATTR);
      if (!url) return;
      img.removeAttribute(HELD_ATTR);
      srcDesc.set.call(img, url);
    }

    function releaseReady() {
      var held = w.document.querySelectorAll('img[' + HELD_ATTR + ']');
      for (var i = 0; i < held.length; i++) {
        var img = held[i];
        var url = img.getAttribute(HELD_ATTR);
        if (!shouldHoldKlaviyoSrc(img, url)) release(img);
      }
    }

    Object.defineProperty(Img.prototype, 'src', {
      configurable: true,
      enumerable: srcDesc.enumerable,
      get: function () {
        return this.getAttribute(HELD_ATTR) || srcDesc.get.call(this);
      },
      set: function (value) {
        var url = value == null ? '' : String(value);
        if (shouldHoldKlaviyoSrc(this, url)) {
          hold(this, url);
          return;
        }
        srcDesc.set.call(this, value);
      }
    });

    El.prototype.setAttribute = function (name, value) {
      if (this instanceof Img && String(name).toLowerCase() === 'src') {
        var url = value == null ? '' : String(value);
        if (shouldHoldKlaviyoSrc(this, url)) {
          hold(this, url);
          return;
        }
      }
      return nativeSetAttribute.apply(this, arguments);
    };

    var observer = new w.MutationObserver(releaseReady);
    observer.observe(w.document.documentElement || w.document, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
    });

    w.setTimeout(function () {
      var leftover = w.document.querySelectorAll('img[' + HELD_ATTR + ']');
      for (var i = 0; i < leftover.length; i++) release(leftover[i]);
    }, SAFETY_RELEASE_MS);

    w.CCKlaviyoImageDefer.releaseReady = releaseReady;
  }

  var api = {
    HELD_ATTR: HELD_ATTR,
    isKlaviyoImageUrl: isKlaviyoImageUrl,
    hasEmbedFormClass: hasEmbedFormClass,
    isOverlayOpenFromBox: isOverlayOpenFromBox,
    isKlaviyoEmbedContext: isKlaviyoEmbedContext,
    shouldHoldKlaviyoSrc: shouldHoldKlaviyoSrc,
    install: install
  };

  root.CCKlaviyoImageDefer = api;

  if (root.document) {
    install(root);
  }
})(typeof window !== 'undefined' ? window : globalThis);
